import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ButtonStyled } from "@nike/nike-design-system-components";
import PgTypeBadge from "./PgTypeBadge";
import SortingRuleChip from "./SortingRuleChip";
import ShipToCell from "./ShipToCell";

const MPG_MAX_VISIBLE_ROWS = 5;

function PgMultiplePgsModal({
  spurRow,
  palletGroups,
  loading = false,
  onClose,
  onOpenIlpns,
  onReassignPg,
  onUnassignPg,
  onReassignSelected,
  onUnassignSelected,
}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const modalRef = useRef(null);
  const tableRef = useRef(null);

  const assignedPgIdsKey = useMemo(
    () => palletGroups.map((r) => r.id).join("\u0001"),
    [palletGroups],
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [spurRow?.id, assignedPgIdsKey]);

  useLayoutEffect(() => {
    const modal = modalRef.current;
    const table = tableRef.current;
    if (!modal || !table) {
      return undefined;
    }

    const applyTableMetrics = () => {
      const tableWidth = table.scrollWidth;
      const theadHeight = table.tHead?.getBoundingClientRect().height ?? 0;
      const bodyRows = table.tBodies[0]?.rows;
      let tbodyHeight = 0;
      let visibleTbodyHeight = 0;

      if (bodyRows?.length) {
        for (let i = 0; i < bodyRows.length; i += 1) {
          const rowHeight = bodyRows[i].getBoundingClientRect().height;
          tbodyHeight += rowHeight;
          if (i < MPG_MAX_VISIBLE_ROWS) {
            visibleTbodyHeight += rowHeight;
          }
        }
      }

      const dataRowCount = palletGroups.length;
      const needsVerticalScroll = dataRowCount > MPG_MAX_VISIBLE_ROWS;
      const displayTbodyHeight = needsVerticalScroll
        ? visibleTbodyHeight
        : tbodyHeight;
      const tableBlockHeight = theadHeight + displayTbodyHeight;

      modal.style.setProperty("--pg-mpg-table-width", `${tableWidth}px`);
      modal.style.setProperty(
        "--pg-mpg-table-block-height",
        `${Math.ceil(tableBlockHeight)}px`,
      );
      modal.style.setProperty("--pg-mpg-data-rows", String(dataRowCount));
    };

    applyTableMetrics();

    const observer = new ResizeObserver(applyTableMetrics);
    observer.observe(table);
    if (table.tBodies[0]) {
      observer.observe(table.tBodies[0]);
    }

    window.addEventListener("resize", applyTableMetrics);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", applyTableMetrics);
    };
  }, [assignedPgIdsKey, spurRow?.id, palletGroups.length]);

  const rows = palletGroups;
  const scrollTableVertically = rows.length > MPG_MAX_VISIBLE_ROWS;
  const rowCount = Math.max(rows.length, 1);
  const selectedCount = selectedIds.size;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleRow = useCallback((pgId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(pgId);
      } else {
        next.delete(pgId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked) => {
      if (checked) {
        setSelectedIds(new Set(rows.map((r) => r.id)));
      } else {
        setSelectedIds(new Set());
      }
    },
    [rows],
  );

  const selectedPgIds = useMemo(() => [...selectedIds], [selectedIds]);

  const reassignLabel =
    selectedCount <= 1
      ? "Reassign Selected"
      : `Reassign Selected (${selectedCount})`;
  const unassignLabel =
    selectedCount <= 1
      ? "Unassign Selected"
      : `Unassign Selected (${selectedCount})`;

  if (!spurRow) {
    return null;
  }

  return (
    <div
      className="pg-modal-backdrop pg-modal-backdrop--mpg"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="pg-modal pg-modal--mpg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pg-mpg-modal-title"
        style={{
          "--pg-mpg-row-count": rowCount,
          "--pg-mpg-data-rows": rows.length,
        }}
      >
        <div className="pg-modal__header">
          <div className="pg-modal__title" id="pg-mpg-modal-title">
            Pallet Groups assigned to {spurRow.spur}
          </div>
          <button
            type="button"
            className="pg-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="pg-modal__body pg-modal__body--mpg">
          <div
            className={
              scrollTableVertically
                ? "pg-mpg-table-wrap pg-mpg-table-wrap--scroll-y"
                : "pg-mpg-table-wrap"
            }
            role="region"
            aria-label="Pallet groups assigned to spur"
            tabIndex={0}
          >
            <table
              ref={tableRef}
              className="pg-modal-table pg-modal-table--mpg"
            >
              <thead>
                <tr>
                  <th className="pg-mpg-col-check" scope="col">
                    <span className="pg-mpg-check-cell">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                        title="Select all"
                        aria-label="Select all pallet groups"
                      />
                    </span>
                  </th>
                  <th className="pg-mpg-col-pg" scope="col">Pallet Group</th>
                  <th className="pg-mpg-col-asn" scope="col">ASN</th>
                  <th className="pg-mpg-col-ship" scope="col">Ship-to</th>
                  <th className="pg-mpg-col-po" scope="col">PO</th>
                  <th className="pg-mpg-col-ilpns" scope="col">Total ILPNs</th>
                  <th className="pg-mpg-col-sort" scope="col">Sorting Rule</th>
                  <th className="pg-mpg-col-type" scope="col">Type</th>
                  <th className="pg-mpg-col-pal" scope="col">Palletised</th>
                  <th className="pg-mpg-col-actions" scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="pg-mpg-empty">
                      Loading pallet groups…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="pg-mpg-empty">
                      No pallet groups on this spur.
                    </td>
                  </tr>
                ) : (
                  rows.map((pg) => (
                    <tr key={pg.id}>
                      <td className="pg-mpg-col-check">
                        <span className="pg-mpg-check-cell">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(pg.id)}
                            onChange={(e) => toggleRow(pg.id, e.target.checked)}
                            aria-label={`Select ${pg.palletGroup}`}
                          />
                        </span>
                      </td>
                      <td className="pg-mpg-col-pg pg-cell-strong">{pg.palletGroup}</td>
                      <td className="pg-mpg-col-asn">{pg.asn}</td>
                      <td className="pg-mpg-col-ship pg-mpg-ship-to">
                        <ShipToCell data={pg} />
                      </td>
                      <td className="pg-mpg-col-po">{pg.po}</td>
                      <td className="pg-mpg-col-ilpns">
                        <button
                          type="button"
                          className="pg-link-cell"
                          onClick={() => onOpenIlpns(pg)}
                        >
                          {pg.totalILPNs} ILPNs
                        </button>
                      </td>
                      <td className="pg-mpg-col-sort">
                        <SortingRuleChip value={pg.sortingRule} />
                      </td>
                      <td className="pg-mpg-col-type">
                        <PgTypeBadge value={pg.type || pg.pgType} />
                      </td>
                      <td className="pg-mpg-col-pal">
                        {pg.palletised ?? 0}/{pg.totalILPNs}
                      </td>
                      <td className="pg-mpg-col-actions">
                        <button
                          type="button"
                          className="pg-btn-mpg-row"
                          onClick={() => onReassignPg(pg)}
                        >
                          Reassign
                        </button>
                        <button
                          type="button"
                          className="pg-btn-mpg-row"
                          onClick={() => onUnassignPg(pg)}
                        >
                          Unassign
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="pg-mpg-tip">
            Tip: tick multiple PGs and use <strong>Reassign Selected</strong> to move
            them to a new spur, or <strong>Unassign Selected</strong> to send them all
            back to the left panel in one step.
          </p>
        </div>
        <div className="pg-modal__footer pg-modal__footer--mpg">
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-apply pg-btn-mpg-footer"
            disabled={loading || selectedCount === 0}
            onClick={() => onReassignSelected(selectedPgIds)}
          >
            {reassignLabel}
          </ButtonStyled>
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-reset pg-btn-mpg-footer"
            disabled={loading || selectedCount === 0}
            onClick={() => onUnassignSelected(selectedPgIds)}
          >
            {unassignLabel}
          </ButtonStyled>
          <ButtonStyled
            appearance="primary"
            type="button"
            className="pg-btn-reset pg-btn-mpg-footer"
            onClick={onClose}
          >
            Close
          </ButtonStyled>
        </div>
      </div>
    </div>
  );
}

export default PgMultiplePgsModal;
